// js/InputHandler.js

// Import dependencies
import GameCore from './GameCore.js';
import GameUI from './GameUI.js';
import AccessibilityManager from './core/AccessibilityManager.js';
import ModalManager from './ModalManager.js';
import AnimationManager from './AnimationManager.js';

/**
 * InputHandler.js - User input handling
 */

export default class InputHandler { // Add export default
  /**
   * @param {GameCore} gameCore
   * @param {GameUI} gameUI
   * @param {AccessibilityManager} accessibilityManager
   * @param {AnimationManager} animationManager // Add AnimationManager
   */
  constructor(gameCore, gameUI, accessibilityManager, animationManager) { // Add animationManager param
    // Type checking for dependencies
    if (!(gameCore instanceof GameCore)) throw new Error('InputHandler requires a GameCore instance.');
    if (!(gameUI instanceof GameUI)) throw new Error('InputHandler requires a GameUI instance.');
    if (!(accessibilityManager instanceof AccessibilityManager)) throw new Error('InputHandler requires an AccessibilityManager instance.');
    if (!(animationManager instanceof AnimationManager)) throw new Error('InputHandler requires an AnimationManager instance.'); // Check AnimationManager

    this.gameCore = gameCore;
    this.gameUI = gameUI;
    this.accessibilityManager = accessibilityManager;
    this.animationManager = animationManager; // Store AnimationManager
    /** @type {ModalManager | null} */
    this.modalManager = null; // Will be set later via setModalManager

    // Bind event handlers
    this.handleTileClick = this.handleTileClick.bind(this);
    this.handleTileDragStart = this.handleTileDragStart.bind(this);
    this.handleTileTouchStart = this.handleTileTouchStart.bind(this);
    this.handleTileKeyDown = this.handleTileKeyDown.bind(this);
    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this.handleTileDragMove = this.handleTileDragMove.bind(this);
    this.handleTileTouchMove = this.handleTileTouchMove.bind(this);
    this.handleTileDragEnd = this.handleTileDragEnd.bind(this);
    this.handleTileTouchEnd = this.handleTileTouchEnd.bind(this);

    // Drag state
    this.draggedTile = null; this.dragOffset = { x: 0, y: 0 }; this.tileRect = null; this.dragClone = null;
    // Selected tile state
    this.selectedTileInfo = null;
    // Event listeners references
    this.docMouseMove = this.handleTileDragMove; this.docMouseUp = this.handleTileDragEnd;
    this.docTouchMove = this.handleTileTouchMove; this.docTouchEnd = this.handleTileTouchEnd;

     // Store original selection during drag
     this.dragOriginalSelectedTileInfo = null;
  }

  setModalManager(modalManager) { this.modalManager = modalManager; }

  clearSelection() {
    if (this.selectedTileInfo) { this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null; }
  }

  attachEventHandlers() {
    const tiles = document.querySelectorAll('.tile');
    if (!tiles || tiles.length === 0) { console.warn('InputHandler: No tiles found.'); return; }
    tiles.forEach(tile => {
      tile.removeEventListener('click', this.handleTileClick);
      tile.removeEventListener('mousedown', this.handleTileDragStart);
      tile.removeEventListener('touchstart', this.handleTileTouchStart);
      tile.removeEventListener('keydown', this.handleTileKeyDown);
      tile.addEventListener('click', this.handleTileClick);
      tile.addEventListener('mousedown', this.handleTileDragStart);
      tile.addEventListener('touchstart', this.handleTileTouchStart, { passive: false });
      tile.addEventListener('keydown', this.handleTileKeyDown);
    });
  }

  handleTileClick(event) {
    if (this.draggedTile) return; // Prevent click during drag
    const clickedTile = event.target;
    if (!clickedTile || !clickedTile.classList.contains('tile')) return;

    if (this.gameCore.hasCompletedTodaysPuzzle()) {
      this.gameUI.showMessage('Puzzle already completed!');
      if (this.selectedTileInfo) { this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null; }
      return;
    }
    if (this.gameCore.isHardMode && this.gameCore.puzzle.swapCount >= this.gameCore.MAX_SWAPS) {
      this.gameUI.showMessage('Swap limit reached!');
      if (this.selectedTileInfo) { this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null; }
      return;
    }

    if (clickedTile.classList.contains('highlight-green')) {
      this.gameUI.announceToScreenReader('This tile is locked.', false);
      if (this.selectedTileInfo) { this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null; this.gameUI.announceToScreenReader('Selection canceled, cannot swap locked tile.', true); }
      return;
    }

    const row = parseInt(clickedTile.dataset.row);
    const col = parseInt(clickedTile.dataset.col);

    if (this.selectedTileInfo === null) {
      // --- First tile selection ---
      this.selectedTileInfo = { row, col, element: clickedTile };
      clickedTile.classList.add('selected');
      this.gameUI.announceToScreenReader(`Selected ${clickedTile.textContent} at row ${row + 1}, column ${col + 1}. Select another tile to swap.`, false);

    } else {
      // --- Second tile - attempt swap ---
      const firstSelectionInfo = { ...this.selectedTileInfo }; // Copy info before clearing
      const firstTileElement = firstSelectionInfo.element;
      this.selectedTileInfo = null; // Clear internal selection state immediately
      firstTileElement.classList.remove('selected'); // Remove visual selection from first tile
      clickedTile.classList.remove('selected'); // Ensure second tile doesn't have selection style

      if (firstSelectionInfo.row === row && firstSelectionInfo.col === col) {
        this.gameUI.announceToScreenReader('Selection canceled.', false);
        return;
      }
      if (firstTileElement.classList.contains('highlight-green')) {
        this.gameUI.showMessage('Cannot swap locked tiles.', 2000);
        this.gameUI.announceToScreenReader('Swap failed. First selected tile is locked.', true);
        return;
      }

      // --- Attempt Swap ---
      const swapResult = this.gameCore.swapTiles(firstSelectionInfo.row, firstSelectionInfo.col, row, col);

      if (swapResult.success) {
        // --- Swap Successful ---
        this.gameUI.announceToScreenReader(`Swapped tiles. ${this.gameCore.puzzle.swapCount} swaps used.`, false);
        // Render grid to show static colors immediately
        this.gameUI.renderGrid();
        // Re-attach handlers to potentially new tile elements
        this.attachEventHandlers();

        // Trigger animation for newly green tiles using returned states
        if (this.animationManager && swapResult.oldState && swapResult.newState) {
          this.animationManager.animateNewlyGreenTiles(swapResult.oldState, swapResult.newState, this.gameCore.gridSize);
        } else if (this.animationManager) {
            console.warn('AnimationManager present but state missing from swap result.');
        }
      } else {
        // --- Swap Failed ---
        if (!this.gameCore.hasCompletedTodaysPuzzle() && (!this.gameCore.isHardMode || this.gameCore.puzzle.swapCount < this.gameCore.MAX_SWAPS)) {
           this.gameUI.showMessage('Cannot swap these tiles.', 2000);
           this.gameUI.announceToScreenReader('Swap failed.', true);
        }
      }
    }
  }


  handleTileDragStart(event) {
    if (event.button !== 0) return;
    const tile = event.target;
    if (!tile || !tile.classList.contains('tile')) return;

    if (this.gameCore.hasCompletedTodaysPuzzle()) { event.preventDefault(); this.gameUI.showMessage('Puzzle already completed!'); return; }
    if (this.gameCore.isHardMode && this.gameCore.puzzle.swapCount >= this.gameCore.MAX_SWAPS) { event.preventDefault(); this.gameUI.showMessage('Swap limit reached!'); return; }
    if (tile.classList.contains('highlight-green')) { event.preventDefault(); return; }

    this.draggedTile = tile;
    this.tileRect = tile.getBoundingClientRect();
    this.dragOffset = { x: event.clientX - this.tileRect.left, y: event.clientY - this.tileRect.top };

    // Store selection state in case drag cancels
    this.dragOriginalSelectedTileInfo = this.selectedTileInfo ? { ...this.selectedTileInfo } : null;
    // If the dragged tile WAS the selected tile, remove visual selection during drag
    if (this.selectedTileInfo && this.selectedTileInfo.element === tile) {
      tile.classList.remove('selected');
    }

    tile.classList.add('dragging');
    this.dragClone = this.gameUI.createDragElement(tile, event.clientX, event.clientY, this.dragOffset, this.tileRect);

    document.addEventListener('mousemove', this.docMouseMove);
    document.addEventListener('mouseup', this.docMouseUp, { once: true });
    event.preventDefault();
  }

  handleTileTouchStart(event) {
    if (event.touches.length !== 1) return;
    const tile = event.target;
    if (!tile || !tile.classList.contains('tile')) return;

    if (this.gameCore.hasCompletedTodaysPuzzle()) { event.preventDefault(); this.gameUI.showMessage('Puzzle already completed!'); return; }
    if (this.gameCore.isHardMode && this.gameCore.puzzle.swapCount >= this.gameCore.MAX_SWAPS) { event.preventDefault(); this.gameUI.showMessage('Swap limit reached!'); return; }
    if (tile.classList.contains('highlight-green')) { event.preventDefault(); return; }

    const touch = event.touches[0];
    this.draggedTile = tile;
    this.tileRect = tile.getBoundingClientRect();
    this.dragOffset = { x: touch.clientX - this.tileRect.left, y: touch.clientY - this.tileRect.top };

    this.dragOriginalSelectedTileInfo = this.selectedTileInfo ? { ...this.selectedTileInfo } : null;
    if (this.selectedTileInfo && this.selectedTileInfo.element === tile) {
      tile.classList.remove('selected');
    }

    tile.classList.add('dragging');
    this.dragClone = this.gameUI.createDragElement(tile, touch.clientX, touch.clientY, this.dragOffset, this.tileRect);

    document.addEventListener('touchmove', this.docTouchMove, { passive: false });
    document.addEventListener('touchend', this.docTouchEnd, { once: true });
    document.addEventListener('touchcancel', this.docTouchEnd, { once: true });
    event.preventDefault();
  }

  handleTileDragMove(event) { if (!this.draggedTile || !this.dragClone) return; this.dragClone.style.left = `${event.clientX - this.dragOffset.x}px`; this.dragClone.style.top = `${event.clientY - this.dragOffset.y}px`; this.gameUI.updateDropTarget(event.clientX, event.clientY, this.draggedTile); event.preventDefault(); }
  handleTileTouchMove(event) { if (!this.draggedTile || !this.dragClone || event.touches.length !== 1) return; const touch = event.touches[0]; this.dragClone.style.left = `${touch.clientX - this.dragOffset.x}px`; this.dragClone.style.top = `${touch.clientY - this.dragOffset.y}px`; this.gameUI.updateDropTarget(touch.clientX, touch.clientY, this.draggedTile); event.preventDefault(); }

  _handleDragEnd(clientX, clientY) {
    if (!this.draggedTile) return;

    const targetTile = this.gameUI.findTileAtPosition(clientX, clientY);
    let swapAttempted = false;
    let swapResult = { success: false }; // Initialize result

    if (targetTile && targetTile !== this.draggedTile &&
        !targetTile.classList.contains('highlight-green') &&
        !this.draggedTile.classList.contains('highlight-green')) {

      const sourceRow = parseInt(this.draggedTile.dataset.row);
      const sourceCol = parseInt(this.draggedTile.dataset.col);
      const targetRow = parseInt(targetTile.dataset.row);
      const targetCol = parseInt(targetTile.dataset.col);

      // --- Attempt Swap ---
      swapResult = this.gameCore.swapTiles(sourceRow, sourceCol, targetRow, targetCol);
      swapAttempted = true;

      if (swapResult.success) {
        // --- Swap Successful ---
        this.selectedTileInfo = null; // Clear selection after successful drag swap
        this.dragOriginalSelectedTileInfo = null; // Clear stored original

        this.gameUI.announceToScreenReader(`Swapped tiles. ${this.gameCore.puzzle.swapCount} swaps used.`, false);
        // Render grid first to show static state
        this.gameUI.renderGrid();
        // Re-attach handlers
        this.attachEventHandlers();

        // Trigger animation using returned states
        if (this.animationManager && swapResult.oldState && swapResult.newState) {
          this.animationManager.animateNewlyGreenTiles(swapResult.oldState, swapResult.newState, this.gameCore.gridSize);
        } else if (this.animationManager) {
            console.warn('AnimationManager present but state missing from swap result.');
        }
      }
    }

    // --- Drag Cleanup and Selection Restoration ---
    if (!swapAttempted || !swapResult.success) {
      // Drag failed or wasn't attempted on a valid target
      const originalSelection = this.dragOriginalSelectedTileInfo;
      // Restore selection ONLY if the drag started FROM the originally selected tile
      if (originalSelection && originalSelection.element === this.draggedTile) {
          // Need to potentially find the element again if grid was re-rendered somehow (unlikely on fail)
          const originalTileElement = this.gameUI.gridContainer?.querySelector(`.tile[data-row="${originalSelection.row}"][data-col="${originalSelection.col}"]`);
          if (originalTileElement && !originalTileElement.classList.contains('highlight-green')) {
              originalTileElement.classList.add('selected');
              // Restore internal state, ensuring element reference is current
              this.selectedTileInfo = { ...originalSelection, element: originalTileElement };
          } else {
              // Original tile is gone or locked, clear selection state
              this.selectedTileInfo = null;
          }
      } else {
          // If drag failed from a NON-selected tile, revert selection state to whatever it was before the drag started
          this.selectedTileInfo = this.dragOriginalSelectedTileInfo;
          // Re-apply visual selection if needed
          if (this.selectedTileInfo && this.selectedTileInfo.element) {
              this.selectedTileInfo.element.classList.add('selected');
          }
      }
    }

    this.cleanupDrag();
    this.dragOriginalSelectedTileInfo = null; // Always clear stored original selection info
  }


  handleTileDragEnd(event) { this._handleDragEnd(event.clientX, event.clientY); event.preventDefault(); }
  handleTileTouchEnd(event) {
    let clientX = 0, clientY = 0;
    if (this.dragClone) { const cloneRect = this.dragClone.getBoundingClientRect(); clientX = cloneRect.left + cloneRect.width / 2; clientY = cloneRect.top + cloneRect.height / 2; }
    else if (event.changedTouches && event.changedTouches.length > 0) { clientX = event.changedTouches[0].clientX; clientY = event.changedTouches[0].clientY; }
    this._handleDragEnd(clientX, clientY);
    if (this.draggedTile) { event.preventDefault(); }
  }

  cleanupDrag() {
    if (this.dragClone) { this.dragClone.remove(); this.dragClone = null; }
    if (this.draggedTile) { this.draggedTile.classList.remove('dragging'); this.draggedTile = null; }
    this.gameUI.updateDropTarget(-1, -1, null);
    document.removeEventListener('mousemove', this.docMouseMove); document.removeEventListener('mouseup', this.docMouseUp);
    document.removeEventListener('touchmove', this.docTouchMove); document.removeEventListener('touchend', this.docTouchEnd); document.removeEventListener('touchcancel', this.docTouchEnd);
    this.tileRect = null;
  }

  handleTileKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.gameUI.isKeyboardMode()) { this.gameUI.setKeyboardModeActive(true); }
      this.handleTileClick({ target: event.target });
    } else if (event.key === 'Escape' && this.selectedTileInfo) {
      event.preventDefault();
      this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null;
      this.gameUI.announceToScreenReader('Selection canceled', true);
    } else if (event.key === 'Tab') {
      this.gameUI.setKeyboardModeActive(false);
    }
  }

  handleGlobalKeydown(event) {
    const activeEl = document.activeElement;
    if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) { return; }
    if (this.modalManager && this.modalManager.isModalOpen()) { return; }

    if (event.key.startsWith('Arrow')) {
      event.preventDefault();
      if (!this.gameUI.isKeyboardMode()) { this.gameUI.setKeyboardModeActive(true); }
      const gridContainer = this.gameUI.gridContainer;
      if (gridContainer && !gridContainer.contains(document.activeElement)) {
          const { row, col } = this.gameUI.keyboardFocusPosition;
          const targetTile = gridContainer.querySelector(`.tile[data-row="${row}"][data-col="${col}"]`);
          if (targetTile) targetTile.focus(); else gridContainer.focus();
      }

      const { row, col } = this.gameUI.keyboardFocusPosition;
      let newRow = row, newCol = col; const gridSize = this.gameCore.gridSize;
      switch (event.key) {
        case 'ArrowUp': newRow = Math.max(0, row - 1); break;
        case 'ArrowDown': newRow = Math.min(gridSize - 1, row + 1); break;
        case 'ArrowLeft': newCol = Math.max(0, col - 1); break;
        case 'ArrowRight': newCol = Math.min(gridSize - 1, col + 1); break;
      }

      if (newRow !== row || newCol !== col) {
        this.gameUI.setKeyboardFocusPosition(newRow, newCol);
        const newFocusedTile = this.gameUI.gridContainer?.querySelector(`.tile[data-row="${newRow}"][data-col="${newCol}"]`);
        if (newFocusedTile) {
          newFocusedTile.focus();
          const letter = newFocusedTile.textContent; const isLocked = newFocusedTile.classList.contains('highlight-green');
          const state = isLocked ? 'locked' : newFocusedTile.classList.contains('highlight-yellow') ? 'correct letter, wrong position' : 'movable';
          this.gameUI.announceToScreenReader(`${letter}, row ${newRow + 1}, column ${newCol + 1}, ${state}.`);
        }
      }
      return;
    }

    switch (event.key.toLowerCase()) {
      case 'r': if (!event.ctrlKey && !event.metaKey) { event.preventDefault(); document.dispatchEvent(new CustomEvent('puzzle-reset-request')); } break;
      case 'h': if (!event.ctrlKey && !event.metaKey) { event.preventDefault(); document.dispatchEvent(new CustomEvent('puzzle-hint-request')); } break;
      case 'escape':
        if (this.selectedTileInfo) { event.preventDefault(); this.selectedTileInfo.element.classList.remove('selected'); this.selectedTileInfo = null; this.gameUI.announceToScreenReader('Selection canceled', true); }
        else if (this.gameUI.isKeyboardMode()) { event.preventDefault(); this.gameUI.setKeyboardModeActive(false); this.gameUI.announceToScreenReader('Keyboard navigation disabled.', false); }
        break;
    }
  }

} // End of InputHandler class
// Removed window export
